import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { adminPath } from '../config/adminRoutes';

const AuthContext = createContext();

const CONTENT_ROLES = ['ADMIN', 'SUPER_ADMIN', 'CONTENT_MANAGER', 'EDITOR'];
const STAFF_ROLES = CONTENT_ROLES;

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function callAdminAuthStart(email, password) {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase não configurado');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-auth-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      action: 'start',
      email: email.trim().toLowerCase(),
      password,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: payload.error || 'Email ou senha incorrectos',
    };
  }

  return {
    success: true,
    email: payload.email,
    emailMasked: payload.emailMasked,
    challengeId: payload.challengeId,
    expiresInMinutes: payload.expiresInMinutes,
  };
}

async function callAdminAuthVerify(email, otp, challengeId) {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase não configurado');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-auth-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      action: 'verify',
      email: email.trim().toLowerCase(),
      otp: String(otp || '').trim().replace(/\s/g, ''),
      challengeId,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: payload.error || 'Código inválido ou expirado. Solicite um novo código.',
    };
  }

  return {
    success: true,
    session: {
      access_token: payload.access_token,
      refresh_token: payload.refresh_token,
    },
    user: payload.user,
  };
}

async function callAdminAuthReset(email) {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase não configurado');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-auth-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      success: false,
      error: payload.error || 'Não foi possível enviar o email de recuperação',
    };
  }

  return {
    success: true,
    message: payload.message || 'Se o email estiver registado, receberá instruções em breve.',
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carrega o perfil (role/nome) a partir da tabela public.profiles.
  // O role NUNCA vem de user_metadata (editável pelo utilizador).
  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) {
      setUser(null);
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('id, full_name, role, is_active')
      .eq('id', authUser.id)
      .single();

    if (!profile?.is_active || !STAFF_ROLES.includes(profile?.role)) {
      await supabase.auth.signOut();
      setUser(null);
      return;
    }

    setUser({
      id: authUser.id,
      email: authUser.email,
      name: profile?.full_name || authUser.email,
      role: profile?.role || 'USER',
    });
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!active) return;
      await loadProfile(session?.user ?? null);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      await loadProfile(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      active = false;
      subscription?.unsubscribe();
    };
  }, [loadProfile]);

  const requestLoginOtp = async (email, password) => {
    try {
      return await callAdminAuthStart(email, password);
    } catch (error) {
      console.error('OTP request error:', error);
      return { success: false, error: 'Erro de conexão. Tente novamente.' };
    }
  };

  const verifyLoginOtp = async (email, otp, challengeId) => {
    try {
      if (!challengeId) {
        return { success: false, error: 'Sessão de verificação expirada. Solicite um novo código.' };
      }

      const result = await callAdminAuthVerify(email, otp, challengeId);
      if (!result.success) {
        return result;
      }

      const { error: sessionError } = await supabase.auth.setSession(result.session);
      if (sessionError || !result.user) {
        return { success: false, error: 'Não foi possível iniciar sessão. Tente novamente.' };
      }

      await loadProfile(result.user);
      return { success: true };
    } catch (error) {
      console.error('OTP verify error:', error);
      return { success: false, error: 'Erro de conexão. Tente novamente.' };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      return await callAdminAuthReset(email);
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: 'Erro de conexão. Tente novamente.' };
    }
  };

  const login = requestLoginOtp;

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    window.location.href = adminPath('login');
  };

  const hasRole = (role) => {
    if (Array.isArray(role)) return role.includes(user?.role);
    return user?.role === role;
  };

  const isAdmin = () => user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const canManageContent = () => CONTENT_ROLES.includes(user?.role);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    requestLoginOtp,
    verifyLoginOtp,
    requestPasswordReset,
    logout,
    hasRole,
    isAdmin,
    canManageContent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
